import {
  DEFAULT_ADMIN_LOCALE,
  isAdminLocale,
  tAdmin,
  type AdminKey,
  type AdminLocale,
} from '@unibyte/i18n/admin';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { apiRequest } from './api.js';
import { useSession } from './session.js';

const STORAGE_KEY = 'unibyte.admin.locale';

interface AdminLocaleState {
  locale: AdminLocale;
  setLocale: (locale: AdminLocale) => void;
  t: (key: AdminKey, params?: Record<string, string | number>) => string;
  text: (el: string, en: string) => string;
  formatDate: (value: string | null | undefined, withTime?: boolean) => string;
}

const AdminLocaleContext = createContext<AdminLocaleState | null>(null);

function readStoredLocale(): AdminLocale | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return isAdminLocale(raw) ? raw : null;
  } catch {
    return null;
  }
}

function detectBrowserLocale(): AdminLocale {
  try {
    const languages = navigator.languages?.length ? navigator.languages : [navigator.language];
    for (const language of languages) {
      if (language.toLowerCase().startsWith('en')) return 'en';
      if (language.toLowerCase().startsWith('el')) return 'el';
    }
  } catch {
    // Fall through.
  }
  return DEFAULT_ADMIN_LOCALE;
}

function applyDocumentLang(locale: AdminLocale): void {
  document.documentElement.lang = locale;
}

export function AdminLocaleProvider({ children }: { children: ReactNode }) {
  const { user } = useSession();
  const [locale, setLocaleState] = useState<AdminLocale>(() => {
    return readStoredLocale() ?? detectBrowserLocale();
  });

  useEffect(() => {
    applyDocumentLang(locale);
  }, [locale]);

  useEffect(() => {
    if (!user) return;
    if (isAdminLocale(user.uiLocale)) {
      setLocaleState(user.uiLocale);
      try {
        window.localStorage.setItem(STORAGE_KEY, user.uiLocale);
      } catch {
        // Memory-only preference.
      }
      applyDocumentLang(user.uiLocale);
    }
  }, [user?.id, user?.uiLocale]);

  const setLocale = useCallback(
    (next: AdminLocale) => {
      setLocaleState(next);
      applyDocumentLang(next);
      try {
        window.localStorage.setItem(STORAGE_KEY, next);
      } catch {
        // Memory-only preference.
      }
      if (user) {
        void apiRequest('/v1/auth/me/ui-locale', {
          method: 'PATCH',
          body: JSON.stringify({ locale: next }),
        }).catch(() => undefined);
      }
    },
    [user],
  );

  const value = useMemo<AdminLocaleState>(
    () => ({
      locale,
      setLocale,
      t: (key, params) => tAdmin(locale, key, params),
      text: (el, en) => (locale === 'en' ? en : el),
      formatDate: (value, withTime = false) => {
        if (!value) return '—';
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return value;
        const intlLocale = locale === 'en' ? 'en-GB' : 'el-GR';
        return withTime
          ? date.toLocaleString(intlLocale, { dateStyle: 'short', timeStyle: 'short' })
          : date.toLocaleDateString(intlLocale);
      },
    }),
    [locale, setLocale],
  );

  return <AdminLocaleContext.Provider value={value}>{children}</AdminLocaleContext.Provider>;
}

export function useAdminLocale(): AdminLocaleState {
  const value = useContext(AdminLocaleContext);
  if (!value) throw new Error('useAdminLocale must be used inside AdminLocaleProvider');
  return value;
}

export function useAdminT(): AdminLocaleState['t'] {
  return useAdminLocale().t;
}

export function LanguageSwitcher(props: { className?: string }) {
  const { locale, setLocale, t } = useAdminLocale();
  return (
    <div
      className={
        props.className ??
        'inline-flex items-center gap-0.5 rounded-[var(--admin-radius-sm)] border border-[color:var(--admin-border)] p-0.5 text-xs dark:border-slate-700'
      }
      role="group"
      aria-label={t('admin.language.selector')}
    >
      {(['el', 'en'] as const).map((code) => (
        <button
          key={code}
          type="button"
          aria-pressed={locale === code}
          onClick={() => setLocale(code)}
          className={
            locale === code
              ? 'admin-mono rounded-[4px] bg-[color:var(--admin-ink,#0b1220)] px-2 py-1 text-[10px] font-medium tracking-[0.08em] text-white dark:bg-white dark:text-slate-900'
              : 'admin-mono rounded-[4px] px-2 py-1 text-[10px] font-medium tracking-[0.08em] text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'
          }
        >
          {code.toUpperCase()}
        </button>
      ))}
    </div>
  );
}
